'use client';

import Image from 'next/image';
import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

interface Transformation {
  id: number;
  beforeImage: string;
  afterImage: string;
  title: string;
  package: string;
}

const transformations: Transformation[] = [
  {
    id: 1,
    beforeImage: '/images/generated/transformation-red-sedan-before.png',
    afterImage: '/images/generated/transformation-red-sedan-after.png',
    title: 'Red Sedan - Full Detail',
    package: 'Premium Package'
  },
  {
    id: 2,
    beforeImage: '/images/generated/transformation-red-sedan-before.png',
    afterImage: '/images/generated/transformation-white-suv-after.png',
    title: 'White SUV - Complete Restoration',
    package: 'Premium Package'
  },
  {
    id: 3,
    beforeImage: '/images/generated/transformation-red-sedan-before.png',
    afterImage: '/images/generated/transformation-red-sedan-after.png',
    title: 'Another Red Vehicle - Express Service',
    package: 'Basic Package'
  },
];

export function TransformationGallery() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);

  const current = transformations[currentIndex];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % transformations.length);
    setSliderPosition(50);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + transformations.length) % transformations.length);
    setSliderPosition(50);
  };

  const handleSliderMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.max(0, Math.min(100, newPosition)));
  };

  return (
    <div data-zylo-id="zylo-e6bf74c09ef7" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="64:5-195:11" data-zylo-component="TransformationGallery" className="space-y-6">
      {/* Before/After Slider */}
      <motion.div data-zylo-id="zylo-06dde51ca95b" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="66:7-120:20" data-zylo-component="TransformationGallery"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative w-full max-w-4xl mx-auto overflow-hidden rounded-lg shadow-2xl"
      >
        <div data-zylo-id="zylo-1a3d5df624b4" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="72:9-119:15" data-zylo-component="TransformationGallery"
          className="relative w-full aspect-video cursor-ew-resize bg-gray-100"
          onMouseMove={handleSliderMove}
        >
          {/* Before Image */}
          <div data-zylo-id="zylo-50f833125193" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="77:11-84:17" data-zylo-component="TransformationGallery" className="absolute inset-0">
            <Image data-zylo-id="zylo-563fbaaf0d6b" data-zylo-kind="media" data-zylo-cap="742" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="78:13-83:15" data-zylo-component="TransformationGallery"
              src={current.beforeImage}
              alt={`Before - ${current.title}`}
              fill
              className="object-cover"
            />
          </div>

          {/* After Image with clip */}
          <div data-zylo-id="zylo-d8a42d732fb0" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="87:11-97:17" data-zylo-component="TransformationGallery"
            className="absolute inset-0 overflow-hidden transition-all"
            style={{ width: `${sliderPosition}%` }}
          >
            <Image data-zylo-id="zylo-be7c03206749" data-zylo-kind="media" data-zylo-cap="742" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="91:13-96:15" data-zylo-component="TransformationGallery"
              src={current.afterImage}
              alt={`After - ${current.title}`}
              fill
              className="object-cover"
            />
          </div>

          {/* Slider Handle */}
          <div data-zylo-id="zylo-102b4c59f054" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="100:11-110:17" data-zylo-component="TransformationGallery"
            className="absolute top-0 bottom-0 w-1 bg-white transition-all"
            style={{ left: `${sliderPosition}%` }}
          >
            <div data-zylo-id="zylo-78d9aa9335e2" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="104:13-109:19" data-zylo-component="TransformationGallery" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-3 shadow-lg">
              <div data-zylo-id="zylo-8dedce1eeff4" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="105:15-108:21" data-zylo-component="TransformationGallery" className="flex gap-2">
                <ChevronLeft data-zylo-id="zylo-d612f8ab8346" data-zylo-kind="icon" data-zylo-cap="86" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="106:17-106:64" data-zylo-component="TransformationGallery" className="w-5 h-5 text-accent" />
                <ChevronRight data-zylo-id="zylo-41c40965ef93" data-zylo-kind="icon" data-zylo-cap="86" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="107:17-107:65" data-zylo-component="TransformationGallery" className="w-5 h-5 text-accent" />
              </div>
            </div>
          </div>

          {/* Labels */}
          <div data-zylo-id="zylo-9fe8a10f9dc2" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="113:11-115:17" data-zylo-component="TransformationGallery" className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded text-sm font-semibold">
            Before
          </div>
          <div data-zylo-id="zylo-a7bc0cadc160" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="116:11-118:17" data-zylo-component="TransformationGallery" className="absolute top-4 right-4 bg-accent/80 text-white px-3 py-1 rounded text-sm font-semibold">
            After
          </div>
        </div>
      </motion.div>

      {/* Details and Navigation */}
      <div data-zylo-id="zylo-a078452aa517" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="123:7-167:13" data-zylo-component="TransformationGallery" className="flex flex-col sm:flex-row items-center justify-between gap-6">
        <div data-zylo-id="zylo-c3c25ca69d61" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="124:9-141:15" data-zylo-component="TransformationGallery" className="text-center sm:text-left">
          <motion.h3 data-zylo-id="zylo-173362124e3d" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="125:11-132:23" data-zylo-component="TransformationGallery"
            key={`title-${currentIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-2xl font-bold text-foreground"
          >
            {current.title}
          </motion.h3>
          <motion.p data-zylo-id="zylo-690986e434ef" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="133:11-140:22" data-zylo-component="TransformationGallery"
            key={`package-${currentIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-accent font-semibold mt-1"
          >
            {current.package}
          </motion.p>
        </div>

        {/* Navigation Buttons */}
        <div data-zylo-id="zylo-0b48e60d491a" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="144:9-166:15" data-zylo-component="TransformationGallery" className="flex gap-3">
          <Button data-zylo-id="zylo-4644d3541d48" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="145:11-152:20" data-zylo-component="TransformationGallery"
            variant="outline"
            size="icon"
            onClick={prevSlide}
            className="rounded-full"
          >
            <ChevronLeft data-zylo-id="zylo-654d1255e788" data-zylo-kind="icon" data-zylo-cap="86" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="151:13-151:48" data-zylo-component="TransformationGallery" className="w-5 h-5" />
          </Button>
          <div data-zylo-id="zylo-145e9f1c2cdd" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="153:11-157:17" data-zylo-component="TransformationGallery" className="flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full">
            <span data-zylo-id="zylo-0003cd95eab2" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="154:13-156:20" data-zylo-component="TransformationGallery" className="text-sm font-semibold">
              {currentIndex + 1} / {transformations.length}
            </span>
          </div>
          <Button data-zylo-id="zylo-c6c846bdc9f8" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="158:11-165:20" data-zylo-component="TransformationGallery"
            variant="outline"
            size="icon"
            onClick={nextSlide}
            className="rounded-full"
          >
            <ChevronRight data-zylo-id="zylo-57a0b740bb50" data-zylo-kind="icon" data-zylo-cap="86" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="164:13-164:49" data-zylo-component="TransformationGallery" className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Thumbnail Navigation */}
      <div data-zylo-id="zylo-681c95f0dc07" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="170:7-194:13" data-zylo-component="TransformationGallery" className="flex gap-3 justify-center overflow-x-auto pb-2">
        {transformations.map((item, index) => (
          <motion.button data-zylo-id="zylo-8364ed9e2745" data-zylo-kind="button" data-zylo-cap="126" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="172:11-192:27" data-zylo-component="TransformationGallery"
            key={item.id}
            onClick={() => {
              setCurrentIndex(index);
              setSliderPosition(50);
            }}
            className={`flex-shrink-0 relative rounded-lg overflow-hidden transition-all ${
              index === currentIndex
                ? 'ring-2 ring-accent scale-105'
                : 'opacity-60 hover:opacity-100'
            }`}
            whileHover={{ scale: 1.05 }}
          >
            <Image data-zylo-id="zylo-424c48f57a93" data-zylo-kind="media" data-zylo-cap="742" data-zylo-file="src/components/transformation-gallery.tsx" data-zylo-loc="185:13-191:15" data-zylo-component="TransformationGallery"
              src={item.afterImage}
              alt={`Thumbnail ${index + 1}`}
              width={100}
              height={70}
              className="object-cover"
            />
          </motion.button>
        ))}
      </div>
    </div>
  );
}
